import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Empty, Popover, Spin } from 'antd';
import { DeleteOutlined, MailOutlined } from '@ant-design/icons';
import DOMPurify from 'dompurify';
import BellIcon from './BellIcon';
import useNotificationCenter from 'hooks/useNotificationCenter';

const AlertsIcon = ({ unreadCount, open, ariaLabel }) => (
	<button
		type="button"
		className="notification-trigger ant-badge"
		aria-label={ariaLabel}
		aria-expanded={open}
		data-test-id="mk-notification-btn"
	>
		<span className="ant-avatar avatar ant-avatar-circle ant-avatar-icon">
			<span className="anticon anticon-bell">
				<BellIcon />
			</span>
		</span>
		{unreadCount > 0 && (
			<span className="ant-scroll-number ant-badge-count ant-badge-multiple-words">
				{unreadCount > 99 ? '99+' : unreadCount}
			</span>
		)}
	</button>
);

const sanitizeHtml = (html) => ({
	__html: DOMPurify.sanitize(html ?? ''),
});

const HeaderNotifications = () => {
	const { t } = useTranslation();
	const {
		timeZone,
		isOpen,
		onlyUnread,
		visibleNotifications,
		groupedNotifications,
		badgeCount,
		isInitialLoading,
		isLoadingMore,
		loadFailed,
		hasReachedEnd,
		canMarkAsRead,
		canClear,
		viewportRef,
		onOpenChange,
		onUnreadFilterChange,
		onScroll,
		retry,
		markAsRead,
		clearAll,
	} = useNotificationCenter();

	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const onKeyDown = (event) => {
			if (event.key === 'Escape') {
				onOpenChange(false);
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [isOpen, onOpenChange]);

	const card = (
		<div className="notification-card" data-test-id="mk-notifications-window">
			<header className="notification-card__header">
				<h2>{t('Notifications')}</h2>
				<Checkbox
					checked={onlyUnread}
					onChange={(event) => onUnreadFilterChange(event.target.checked)}
					data-test-id="mk-notifications-checkbox-only-unread"
				>
					{t('Only show unread')}
				</Checkbox>
			</header>

			<div
				ref={viewportRef}
				className="notification-card__viewport"
				role="list"
				aria-busy={isInitialLoading || isLoadingMore}
				aria-label={t('Notifications')}
				onScroll={onScroll}
			>
				{isInitialLoading ? (
					<div className="notification-card__state" role="status">
						<Spin />
					</div>
				) : (
					<>
						{groupedNotifications.map((section) => (
							<section
								key={section.key}
								className="notification-section"
								data-test-id={`mk-notifications-section-${section.key}`}
							>
								<div className="notification-section__divider" role="separator">
									{t(section.key)}
								</div>
								{section.items.map((notification) => (
									<article
										key={notification.messageId}
										className={`notification-item${notification.isRead ? '' : ' notification-item--unread'}`}
										role="listitem"
										data-test-id={`mk-notification-item-${notification.messageId}`}
									>
										<div className="notification-item__meta">
											{!notification.isRead && (
												<>
													<span className="notification-item__unread-dot" aria-hidden="true" />
													<span className="sr-only">{t('Unread')}</span>
												</>
											)}
											{notification.timestamp && (
												<time
													dateTime={notification.timestamp}
													data-test-id={`mk-notifications-datetime-${notification.messageId}`}
												>
													{new Date(notification.timestamp).toLocaleString('en-GB', {
														timeZone,
													})}{' '}
													{timeZone}
												</time>
											)}
										</div>
										{notification.title && (
											<h3
												data-test-id={`mk-notifications-title-${notification.messageId}`}
												dangerouslySetInnerHTML={sanitizeHtml(notification.title)}
											/>
										)}
										{notification.text && (
											<p
												data-test-id={`mk-notifications-text-${notification.messageId}`}
												dangerouslySetInnerHTML={sanitizeHtml(notification.text)}
											/>
										)}
									</article>
								))}
							</section>
						))}

						{visibleNotifications.length === 0 && !isLoadingMore && !loadFailed && (
							<div className="notification-card__empty">
								<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
							</div>
						)}

						{isLoadingMore && (
							<div
								className="notification-card__state notification-card__state--compact"
								role="status"
								data-test-id="mk-notifications-loading-more"
							>
								<Spin size="small" />
							</div>
						)}

						{loadFailed && (
							<div className="notification-card__state notification-card__error" role="alert">
								<span>{t('Unable to load notifications')}</span>
								<Button type="link" size="small" onClick={retry}>
									{t('Retry')}
								</Button>
							</div>
						)}

						{hasReachedEnd && (
							<p className="notification-card__end" data-test-id="mk-notifications-end-message">
								{t('That’s all your notifications from the last 30 days')}
							</p>
						)}
					</>
				)}
			</div>

			<footer className="notification-card__footer">
				<Button
					size="small"
					disabled={!canMarkAsRead}
					onClick={markAsRead}
					icon={<MailOutlined />}
					data-test-id="mk-notifications-mark-read-btn"
				>
					{t('Mark as read')}
				</Button>
				<Button
					size="small"
					danger
					disabled={!canClear}
					onClick={clearAll}
					icon={<DeleteOutlined />}
					data-test-id="mk-notifications-clear-all-btn"
				>
					{t('Clear all')}
				</Button>
			</footer>
		</div>
	);

	return (
		<div className="logato_zone-notification text-null ml-12">
			<Popover
				trigger="click"
				placement="bottomRight"
				open={isOpen}
				onOpenChange={onOpenChange}
				overlayClassName="notification-center-popover"
				content={card}
				arrow={false}
			>
				<span>
					<AlertsIcon unreadCount={badgeCount} open={isOpen} ariaLabel={t('Notifications')} />
				</span>
			</Popover>
		</div>
	);
};

export default HeaderNotifications;
